import { useEffect, useRef, useState } from 'react'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { SUBMENU_DEFAULT_VALUE } from '../../../../constants/route'
import {
  useFilterStateLoader,
  useSubMenuActions,
  useTableConfigLoader,
  useTableLoader,
} from '../../../../hooks'
import { isEqualPagination } from '../../../../utils/pagination'
import { useLocationPagination } from '../../../Pagination'
import { loadTableData } from '../../Table.utils'

export function useAsyncTable({
  beforeLoad,
  containerRef,
  countApi,
  entity,
  extraFilters,
  filterParams,
  filterStateApi,
  getListApiParams,
  getPermanentFilters,
  getTableParams,
  handlePreloadInit,
  handleSetSubMenu,
  isLocalFilterState,
  isLocalPagination,
  itemIdKey,
  listApi,
  resetKey,
  saveExtras,
  sub = SUBMENU_DEFAULT_VALUE,
  tableConfigApi,
  transformInitialFilters,
  transformSaveFilters,
  withoutSubMenu,
}) {
  const { cleanSubMenu, setSecondaryMenu } = useSubMenuActions()

  const { pagination, setPageSize, setPagination } = useLocationPagination({
    isUrlBound: !isLocalPagination,
  })

  const [isInitiated, setInitiated] = useState(false)

  const resetKeyRef = useRef(resetKey)

  function handleChangeSize(nextSize) {
    setPageSize(nextSize)
  }

  function setSubMenu(subMenu) {
    setSecondaryMenu(subMenu)
  }

  const {
    availableFilters,
    columns,
    handleLoadTableConfig,
    handleSetColumns,
    handleSetDense,
    handleSetPreview,
    handleSetView,
    isDense,
    preview,
    requiredColumns,
    view,
  } = useTableConfigLoader({
    entity,
    handleSetSubMenu: withoutSubMenu ? null : handleSetSubMenu || setSubMenu,
    tableConfigApi,
  })

  const {
    filterValues,
    handleLoadFilterState,
    handleResetFilterValues,
    handleResetOrdering,
    handleSetFilterValues,
    handleSetOrdering,
    handleSetSize,
    hasFilterChanged,
    ordering,
  } = useFilterStateLoader({
    beforeLoad,
    entity,
    filterStateApi,
    getPermanentFilters,
    handleChangeSize,
    isLocal: isLocalFilterState,
    pagination,
    saveExtras,
    sub,
    transformInitialFilters,
    transformSaveFilters,
  })

  async function handleTableInit() {
    setInitiated(false)

    const preload = handlePreloadInit?.() || Promise.resolve()

    return preload.then(() =>
      Promise.all([handleLoadTableConfig(), handleLoadFilterState()])
    )
  }

  const loader = useTableLoader({
    countApi,
    entity,
    getListApiParams,
    handleTableInit,
    itemIdKey,
    listApi,
    setPagination,
    onServerInternalError: handleResetFilterValues,
  })

  const {
    handleLoadInit,
    handleLoadPage,
    handleReload: handleReloadPage,
    handleUpdateItem,
    isLoadedInit,
    isLoadFailed,
    isLoadingPage,
    items,
    loadGenerationRef,
    orderRef,
    paginationRef,
    permissions,
    serverError,
  } = loader

  // `useAsyncCallback` never settles a failed page load, so `isInitiated`
  // alone would wait forever after a failed initial load.
  const isInitialLoadSettled = isInitiated || isLoadFailed

  async function handleReload() {
    const generation = loadGenerationRef.current

    const result = await handleReloadPage()

    // The reload that recovers a table whose initial load failed is the only
    // one that can lift its skeleton — the init effect waits for that load.
    if (result && generation === loadGenerationRef.current) {
      setInitiated(true)
    }

    return result
  }

  const params = getTableParams
    ? getTableParams({ filterParams, filterValues })
    : { filters: { ...(filterParams || filterValues), ...extraFilters } }

  useEffect(() => {
    if (isLoadedInit) {
      const generation = loadGenerationRef.current

      loadTableData(loader, params ?? EMPTY_OBJECT, pagination, ordering)?.then(
        () => {
          // A load superseded by a newer init must not lift that init's skeleton.
          if (generation === loadGenerationRef.current) {
            setInitiated(true)
          }
        }
      )
    }
  }, [isLoadedInit, ordering, params])

  useEffect(() => {
    // `resetKeyRef.current === resetKey` guards against reloading while the
    // reset key (e.g. the open Documents folder) is switching: the init effect
    // above reloads the new context from scratch, whereas a page-change reload
    // here would run with the previous context's `paramsRef.current` and clobber
    // the new folder with the old one
    if (
      resetKeyRef.current === resetKey &&
      isLoadedInit &&
      pagination &&
      paginationRef.current &&
      !isEqualPagination(pagination, paginationRef.current)
    ) {
      handleLoadPage(pagination, orderRef.current).then(() => {
        containerRef?.current?.scrollTo({ behavior: 'smooth', top: 0 })
      })
    }
  }, [pagination])

  // Sync after the `[pagination]` effect so that on the folder-switch render it
  // still sees the previous key and skips the stale reload.
  useEffect(() => {
    resetKeyRef.current = resetKey
  }, [resetKey])

  useEffect(() => {
    if (!withoutSubMenu) {
      return function cleanup() {
        cleanSubMenu()
      }
    }
  }, [withoutSubMenu])

  return {
    availableFilters,
    columns,
    filterValues,
    handleLoadInit,
    handleReload,
    handleResetOrdering,
    handleSetColumns,
    handleSetDense,
    handleSetFilterValues,
    handleSetOrdering,
    handleSetPagination: setPagination,
    handleSetPreview,
    handleSetSize,
    handleSetView,
    handleUpdateItem,
    hasFilterChanged,
    isDense,
    isInitialLoadSettled,
    isInitiated,
    isLoadedInit,
    isLoading: !isInitiated,
    isLoadingPage,
    // A page load after the initial one settled, the retry of a failed one
    // included: `useReloadCountersAfterRefetch` must see that retry end.
    isRefetching: isLoadingPage && isInitialLoadSettled,
    items,
    ordering,
    pagination,
    permissions,
    preview,
    requiredColumns,
    serverError,
    view,
  }
}
